/* CanvasTooltip */
import React from 'react';
import {
    Card, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography,
} from '@mui/material';

export default function CanvasTooltip(props) {
    const { label, object, mouseEvent } = props;
    // A ref, not state: the tooltip's own mouse handlers need to cancel a hide that was
    // scheduled by the effect below, and a state value would be read from a stale closure.
    const timerRef = React.useRef(null);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    const [visibility, setVisibility] = React.useState(false);

    React.useEffect(() => {
        willReceivePropsAnalog(props);
    }, [label, object, mouseEvent]);

    function willReceivePropsAnalog(nextProps) {
        const nextMouseEvent = nextProps.mouseEvent;
        let nextPos = position;

        if (nextMouseEvent) {
            switch (nextMouseEvent.type) {
                case 'mouseup':
                    // Clear timer after click (for display or hide -- although hiding timers
                    // timeout immediately, so not really relevant). This is so if you have moused
                    // over an atom (starting the timer) and then click it, the tooltip doesn't
                    // appear. We do not explicitly hide the tooltip here.
                    clearTimer();
                    break;
                case 'mouseenter': // maybe show AtomSpy tooltip
                    // 1. First clear the timeout if we have entered a new atom.  This is so if we
                    // are waving the mouse around a number of atoms, it waits the full delay from
                    // the time you enter the last atom.
                    // * Update the label and position
                    // 2. Then actually set the timeout to show the tooltip after the tooltip delay
                    nextPos = getMousePos(nextMouseEvent);
                    clearTimer();
                    if (!visibility) {
                        timerRef.current = setTimeout(show, CanvasTooltip.delay);
                    }
                    break;
                case 'mousemove': // move the tooltip with the mouse
                    nextPos = getMousePos(nextMouseEvent);
                    break;
                case 'mouseleave': // cancel the tooltip, unless we moved into a neighbor
                    // Prevent tooltip from closing if you move onto a neighboring atom.  The
                    // timeout value of 1 causes it to work only for directly neighboring atoms.
                    clearTimer();
                    // If we triggered the timer by mousing over an atom, we need to cancel it if
                    // we leave the atom before it finishes.
                    if (visibility) {
                        timerRef.current = setTimeout(hide, 100);
                    }
                    break;
                default:
                    break;
            }
        } else {
            // null mouseEvent is a signal to hide the tooltip
            clearTimer();
            setVisibility(false);
        }
        setPosition(nextPos);
    }

    function clearTimer() {
        clearTimeout(timerRef.current);
        timerRef.current = null;
    }

    // Moving off the atom schedules hide() 100ms later, and the 3D canvas sends no further
    // events once the pointer is over this overlay, so without these handlers the tooltip
    // always closes as you move towards it. Entering cancels that pending hide; leaving the
    // tooltip is then the only thing that closes it, since the canvas has already unhovered
    // the atom and will not send another mouseleave.
    //
    // Reaching the tooltip at all depends on the CSS bridge described at the render below.
    function handleTooltipEnter() {
        clearTimer();
    }

    function handleTooltipLeave() {
        clearTimer();
        hide();
    }

    function getMousePos(event) {
        if (typeof event.clientX === 'undefined') {
            return {
                left: event.touches[0].clientX,
                top: event.touches[0].clientY,
            };
        }

        return {
            left: event.clientX,
            top: event.clientY,
        };
    }

    function show() {
        setVisibility(true);
    }

    function hide() {
        setVisibility(false);
    }

    return (visibility ? (
        <>
            {/*
              * .canvasPopup sets margin-left: 20px, so the tooltip is drawn 20px right of the
              * cursor with bare canvas in between. The .canvasTooltip::before rule in
              * public/css/style.css lays a transparent bridge across that strip, extending this
              * element's hit region so the pointer enters it -- and fires onMouseEnter below --
              * before the 100ms hide can run. Drop that rule and the tooltip becomes a race:
              * reachable with a brisk direct move, gone if you pause mid-gap or approach from
              * the left. The two belong together.
              */}
            <div
                className="canvasPopup canvasTooltip"
                style={position}
                onMouseEnter={handleTooltipEnter}
                onMouseLeave={handleTooltipLeave}
            >
                <Card sx={{ padding: '2px 6px 2px 6px' }}>
                    {
                     // Most of the data comes from buildAtomSpyContent in UIManager.
                     // The idea is that data would sent in an object with a type property data,
                     // currently only the "table" type is being used it has properties:
                     // (table, header, tableHeader, title, tableRow)

                     // There are other labels that don't use the type property:
                     // functional group halos from display_tools pass their description as html
                     // and hydrogen bonds and crystal waters which their label as a string.
                    }
                    {label?.type === 'table' && (
                        <>
                            <Typography sx={{ fontWeight: 'bold' }} align="center" variant="h6">
                                {label.title}
                            </Typography>
                            <TableContainer sx={{ padding: '4px 6px 0 0' }}>
                                <Table size="small" align="center" padding="none">
                                    {!!label.header && (
                                        <TableHead sx={{ padding: '4px 6px 4px 6px' }}>
                                            <TableRow sx={{ padding: '4px 6px 4px 6px' }}>
                                                <TableCell style={{ fontWeight: 'bold', padding: '0 6px 0 16px' }}>
                                                    {label.header[0]}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', padding: '0 6px 0 16px' }}>
                                                    {label.header[1]}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                    )}
                                    <TableBody>
                                        {label.body.map((row, i) => (
                                            <TableRow key={`${i.toString()}_${row[0]}`}>
                                                <TableCell sx={{ padding: '4px 6px 4px 6px' }}>
                                                    {row[0]}
                                                </TableCell>
                                                <TableCell sx={{ padding: '4px 6px 4px 6px' }} align="center">
                                                    {row[1]}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            { !!label.extra && label.extra }
                        </>
                    )}
                    {typeof (label) === 'string' && (
                        <Typography variant="body2" dangerouslySetInnerHTML={{ __html: label }} />
                    )}
                </Card>
            </div>
        </>
    ) : false);
}

CanvasTooltip.delay = 500;
